import type { Draft } from "immer";
import { immerable } from "immer";
import type { SelectableEntity } from "@/pages/human-resources/time-management/components/display-filters/model/SelectableEntity.ts";
import type { ContractType } from "@/pages/human-resources/time-management/business/contract/ContractType.ts";
import type { Employee } from "@/pages/human-resources/time-management/business/Employee.ts";
import type { Schedule } from "@/pages/human-resources/time-management/business/schedule/Schedule.ts";
import type { Section } from "@/pages/human-resources/time-management/business/Section.ts";
import type { EmployeeSchedules } from "@/pages/human-resources/time-management/business/EmployeeSchedules.ts";
import type { Contract } from "@/pages/human-resources/time-management/business/contract/Contract.ts";

export interface SelectionState {
  sectionsSelection: SelectableEntity[];
  employeesSelection: SelectableEntity[];
  rolesSelection: SelectableEntity[];
  contractTypesSelection: SelectableEntity[];
  hideAbsents: boolean;
}

export class FilteredOutEntities {
  [immerable] = true;

  constructor(
    public readonly sectionsIds: Set<string> = new Set(),
    public readonly employeesIds: Set<string> = new Set(),
    public readonly rolesIds: Set<string> = new Set(),
    public readonly contractTypesIds: Set<string> = new Set(),
    public readonly hideAbsents = false,
  ) {}

  public static createFromFavoriteSections(
    sections: Section[],
  ): FilteredOutEntities {
    const hasFavorites = sections.some((section) => section.favorite);
    const filteredOutSectionsIds = new Set<string>(
      hasFavorites ? sections.filter((s) => !s.favorite).map((s) => s.id) : [],
    );
    return new FilteredOutEntities(filteredOutSectionsIds);
  }

  public isActive(): boolean {
    return (
      this.hideAbsents ||
      [
        this.sectionsIds,
        this.employeesIds,
        this.rolesIds,
        this.contractTypesIds,
      ]
        .map((s) => s.size)
        .reduce((a, b) => a + b, 0) > 0
    );
  }

  public isSectionIdVisible(id: string): boolean {
    return !this.sectionsIds.has(id);
  }

  public isRoleIdVisible(id: string): boolean {
    return !this.rolesIds.has(id);
  }

  public isContractTypeVisible(contractType: ContractType): boolean {
    return !this.contractTypesIds.has(contractType);
  }

  public isEmployeeVisible(employee: Employee): boolean {
    return !this.employeesIds.has(employee.id);
  }

  public shouldShowEmployeeSchedules(
    employeeSchedules: EmployeeSchedules,
  ): boolean {
    if (!this.isAnyEmployeeScheduleVisible(employeeSchedules)) {
      return false;
    }

    return employeeSchedules.employee
      .mainSiteContracts()
      .some((c) => this.shouldShowContract(c));
  }

  private shouldShowContract(contract: Contract): boolean {
    return (
      !this.sectionsIds.has(contract.sectionId) &&
      !this.rolesIds.has(contract.roleId) &&
      !this.contractTypesIds.has(contract.contractType)
    );
  }

  private isAnyEmployeeScheduleVisible(
    employeeSchedules: EmployeeSchedules,
  ): boolean {
    return (
      this.isEmployeeVisible(employeeSchedules.employee) &&
      this.isAnyScheduleVisible(employeeSchedules.getMainSchedules())
    );
  }

  private isAnyScheduleVisible(schedules: Schedule[]): boolean {
    if (!this.hideAbsents) return true;
    return schedules.some((s) => s.hasWork());
  }
}

export function updateFilteredOutEntities(
  filteredOut: Draft<FilteredOutEntities>,
  state: SelectionState,
): void {
  const listIdsToHide = (sels: SelectableEntity[]): string[] =>
    sels.filter((e) => !e.selected).map((e) => e.id);
  filteredOut.sectionsIds = new Set(listIdsToHide(state.sectionsSelection));
  filteredOut.employeesIds = new Set(listIdsToHide(state.employeesSelection));
  filteredOut.rolesIds = new Set(listIdsToHide(state.rolesSelection));
  filteredOut.contractTypesIds = new Set(
    listIdsToHide(state.contractTypesSelection),
  );
  filteredOut.hideAbsents = state.hideAbsents;
}
